/**
 * Server functions for the active organization's billing. Server-only.
 * Each reads the session itself (a route guard does NOT protect a server fn).
 */
import { createServerFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { and, eq } from 'drizzle-orm'
import { z } from 'zod'

import { db } from '@/db'
import { member, organization } from '@/db/auth-schema'
import { organizationBilling } from '@/db/schema'
import {
  cycleRemainingBytes,
  isEntitled,
  splitCycleUsage,
  totalRemainingBytes,
} from '@/lib/billing/billing'
import { SUPPORT_PAGE_DISPLAY } from '@/lib/contact'
import { PublicError } from '@/lib/errors'
import { personalOrgFor } from '@/lib/org/org'
import { requireOrgMember } from '@/lib/org/require-org.server'
import { softDeleteOrg } from '@/lib/org/soft-delete.server'
import { auth } from '@/lib/server/auth'
import { emitBusinessEvent } from '@/lib/telemetry/business.server'
import { parseInput, id as zId } from '@/lib/validation'

/**
 * Auth context for the `_authenticated` guard. Returns the signed-in user and
 * guarantees they have at least one organization (the billing entity): if a
 * user ends up orgless — e.g. their only org was deleted by a co-owner — a
 * fresh personal org is provisioned so funds always have an owner. Returns null
 * when there's no session (the guard then redirects to /login).
 *
 * Also returns the ACTIVE org, so authenticated routes can key and prefetch
 * their queries in `loader` instead of waiting on a client-side
 * `useActiveOrganization()` round trip first. `activeOrg` is deliberately
 * derived from `session.session.activeOrganizationId` ONLY — the same field
 * `requireOrgMember` uses — so a route loader can never key a query under an org
 * the server fns would refuse. It is null when the session carries no active org
 * (a just-signed-up user, until the switcher sets one); loaders must skip
 * prefetching in that case rather than calling a fn that would throw 400.
 */
export const getAuthContext = createServerFn({ method: 'GET' }).handler(
  async () => {
    const { headers } = getRequest()
    const session = await auth.api.getSession({ headers })
    if (!session) return null

    const orgs = await auth.api.listOrganizations({ headers })
    if (orgs.length === 0) {
      const { name, slug } = personalOrgFor(session.user)
      try {
        await auth.api.createOrganization({
          body: { name, slug, userId: session.user.id },
        })
      } catch {
        // Slug race (a concurrent request already recreated it) — ignore;
        // the client org list/switcher will pick up the existing org.
      }
    }

    // Resolve against the freshly-read org list so a stale active-org id (org
    // deleted, membership revoked) yields null rather than an org the user can
    // no longer reach — matching what `requireOrgMember` would decide.
    const activeOrgId = session.session.activeOrganizationId ?? null
    const active = activeOrgId
      ? (orgs.find((o) => o.id === activeOrgId) ?? null)
      : null

    // Only the identity fields, never the Better Auth user row: this context is
    // serialized into every authenticated page load (SSR payload + client-nav
    // RPC), and the row carries `role`, `banned`, `banReason`, `banExpires`.
    // Add a field here when a consumer actually needs it.
    return {
      user: {
        id: session.user.id,
        name: session.user.name,
        email: session.user.email,
        // `role` ONLY — it decides whether the sidebar renders the admin link,
        // and the client already received it via `useSession()` before this, so
        // this is the same disclosure on a different transport. Reading it from
        // here instead lets the sidebar render identically on server and client
        // (useSession is empty during SSR, which was a hydration mismatch on
        // every authenticated page). Purely cosmetic: `/admin`'s beforeLoad
        // re-checks server-side via getAdminContext. Still NOT the whole row —
        // `banned`/`banReason`/`banExpires` must not reach the page source.
        role: session.user.role ?? null,
      },
      activeOrg: active
        ? { id: active.id, name: active.name, slug: active.slug }
        : null,
    }
  },
)

/**
 * Soft-delete an organization (owner-initiated). Owner-only. Refuses while the
 * org still holds a positive data balance — a soft-delete forfeits access to
 * that balance, so we hard-block it (matching PRODUCTION_CHECKLIST) and tell the
 * user to spend it down or contact support first. Then delegates the mechanics
 * to `softDeleteOrg`.
 */
export const deleteOrganization = createServerFn({ method: 'POST' })
  .validator((d: { organizationId: string }) =>
    parseInput(z.object({ organizationId: zId }), d),
  )
  .handler(async ({ data }) => {
    const { headers } = getRequest()
    const session = await auth.api.getSession({ headers })
    if (!session) throw new PublicError('Not authenticated', 401)
    const { organizationId } = data

    const [org] = await db
      .select()
      .from(organization)
      .where(eq(organization.id, organizationId))
      .limit(1)
    if (!org || org.deletedAt)
      throw new PublicError('Organization not found', 404)

    // Only an owner may delete (a route guard can't protect a server fn).
    const [m] = await db
      .select({ role: member.role })
      .from(member)
      .where(
        and(
          eq(member.organizationId, organizationId),
          eq(member.userId, session.user.id),
        ),
      )
      .limit(1)
    if (m?.role !== 'owner') {
      throw new PublicError('Only an organization owner can delete it.', 403)
    }

    // A personal org would just be re-provisioned on the next load — refuse.
    if (org.slug === personalOrgFor(session.user).slug) {
      throw new PublicError('You can’t delete your personal organization.', 400)
    }

    // Funds guard (hard block): a soft-delete severs access to the wallet, so
    // deleting an org that still has data would silently forfeit it. This is a
    // fast pre-check for the common case; the authoritative, atomic guard runs
    // inside softDeleteOrg's tx (under a FOR UPDATE lock) so a Stripe credit
    // racing this check can't strand a balance in the tombstoned org. The UI
    // also warns, but the server is the source of truth.
    const balanceError =
      'This organization still has a data balance. Use up the remaining ' +
      `data first, or contact us at ${SUPPORT_PAGE_DISPLAY}, before deleting it.`
    // Any active subscription is cancelled as part of the teardown (see
    // softDeleteOrg), so deletion is never blocked on it. Only the remaining
    // allowance funds guard remains a hard block (a soft-delete forfeits it).
    const wallet = await orgWallet(organizationId)
    if (
      // CYCLE allowance only. Non-expiring credit deliberately does NOT block:
      // it never drains on its own, so blocking on it would trap the org (and,
      // via deleteAccount, the USER) forever — a privacy problem, not just an
      // inconvenience. Forfeiting credit is the customer's call, so the UI
      // acknowledges the amount instead of the server refusing.
      cycleRemainingBytes(
        wallet.cycleAllowanceBytes,
        wallet.groupUsedBytes,
        wallet.groupBaselineBytes,
      ) > 0
    ) {
      throw new PublicError(balanceError, 400)
    }

    await softDeleteOrg({ id: org.id, slug: org.slug }, { balanceError })

    emitBusinessEvent('org.delete', {
      org_id: org.id,
      slug: org.slug,
      actor: 'user',
      user_id: session.user.id,
    })
    return { ok: true as const }
  })

/** Cycle snapshot for an org (allowance/usage + subscription); ensures the row
 *  exists. Callers compute `remaining` via the pure fn. */
async function orgWallet(organizationId: string): Promise<{
  cycleAllowanceBytes: number
  creditBytes: number
  groupUsedBytes: number
  groupBaselineBytes: number
  subscriptionRef: string | null
  planPeriodEnd: Date | null
}> {
  await db
    .insert(organizationBilling)
    .values({ organizationId })
    .onConflictDoNothing()
  const [row] = await db
    .select({
      cycleAllowanceBytes: organizationBilling.cycleAllowanceBytes,
      creditBytes: organizationBilling.creditBytes,
      groupUsedBytes: organizationBilling.groupUsedBytes,
      groupBaselineBytes: organizationBilling.groupBaselineBytes,
      subscriptionRef: organizationBilling.subscriptionRef,
      planPeriodEnd: organizationBilling.planPeriodEnd,
    })
    .from(organizationBilling)
    .where(eq(organizationBilling.organizationId, organizationId))
    .limit(1)
  return (
    row ?? {
      cycleAllowanceBytes: 0,
      creditBytes: 0,
      groupUsedBytes: 0,
      groupBaselineBytes: 0,
      subscriptionRef: null,
      planPeriodEnd: null,
    }
  )
}

export const getActiveOrgBalance = createServerFn({ method: 'GET' }).handler(
  async () => {
    // Reuse the shared guard for the live-membership check. It throws
    // PublicError on no session / no active org / removed member; the balance
    // screens degrade to a null-org shape in those cases rather than erroring.
    let organizationId: string
    try {
      ;({ organizationId } = await requireOrgMember())
    } catch (e) {
      if (e instanceof PublicError) {
        return {
          organizationId: null,
          remainingBytes: 0,
          cycleRemainingBytes: 0,
          creditRemainingBytes: 0,
          entitled: false,
        }
      }
      throw e
    }

    const wallet = await orgWallet(organizationId)
    return {
      organizationId,
      // Total usable capacity: this cycle's allowance plus non-expiring credit
      // (clamped at 0 by the pure fn).
      remainingBytes: totalRemainingBytes(
        wallet.cycleAllowanceBytes,
        wallet.creditBytes,
        wallet.groupUsedBytes,
        wallet.groupBaselineBytes,
      ),
      // Split out so the delete affordance can mirror the SERVER guard exactly:
      // it blocks on the expiring allowance only, and treats credit as
      // forfeitable with an acknowledgement. A UI that disabled on the total
      // would refuse a deletion the server would happily accept.
      cycleRemainingBytes: cycleRemainingBytes(
        wallet.cycleAllowanceBytes,
        wallet.groupUsedBytes,
        wallet.groupBaselineBytes,
      ),
      creditRemainingBytes: splitCycleUsage(
        wallet.cycleAllowanceBytes,
        wallet.creditBytes,
        wallet.groupUsedBytes,
        wallet.groupBaselineBytes,
      ).creditRemaining,
      // Whether an active subscription currently unlocks the network.
      entitled: isEntitled(wallet, new Date()),
    }
  },
)
